/* Unified read-only report renderer — used by both the authed Report Detail page and the public
   shared-report page. Handles both SundayBuilder™ (worship plans) and Parish Intelligence™
   report shapes. This is display only — no hymn feedback / regenerate buttons. */

import React from "react";
import ReportView from "./ReportView";

export default function ReportRenderer({ report, meta = {}, product }) {
  if (!report) return null;
  if (product === "parish_intelligence") {
    return <ReportView report={report} meta={meta} />;
  }
  // SundayBuilder™ worship plan renderer (read-only mirror of WorshipPlanner's plan section)
  return <WorshipPlanReadOnly plan={report} meta={meta} />;
}

function WorshipPlanReadOnly({ plan, meta }) {
  return (
    <div className="report-print-area wp-plan" data-testid="report-worship-plan">
      <div className="report-print-brand">
        <img src="/brand/li-logo-sm.png" alt="Liturgical Intelligence™" onError={(e) => { e.target.style.display = "none"; }} />
        <div className="rpb-t">
          <b>SundayBuilder™ Worship Plan</b>
          <span>Liturgical Intelligence™ · Ordo AI™</span>
        </div>
      </div>

      <h1 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 34, margin: "12px 0 8px", color: "#1F2A44" }}>
        {plan.title || meta.title || "Worship Plan"}
      </h1>
      {plan.scope && <div style={{ fontSize: 13, color: "#8a8576", marginBottom: 4 }}>{plan.scope}</div>}
      {meta.church && <div style={{ fontSize: 14, color: "#5c574a", marginBottom: 18 }}>{meta.church}</div>}
      {plan.overview && <p style={{ lineHeight: 1.7, color: "#2a2a2a" }}>{plan.overview}</p>}

      {plan.season_overview && <Block title="Season Overview" text={plan.season_overview} />}
      {plan.annual_strategy && <Block title="Annual Worship Strategy" text={plan.annual_strategy} />}
      {plan.music_strategy && <Block title="Music Strategy" text={plan.music_strategy} />}
      {plan.choir_rehearsal_plan && <Block title="Choir & Rehearsal Planning" text={plan.choir_rehearsal_plan} />}

      {(plan.services || []).map((s, i) => (
        <div key={i} style={{ marginTop: 22, paddingTop: 16, borderTop: "1px solid #ece2c8" }}>
          <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 22, margin: "0 0 6px", color: "#1F2A44" }}>{s.name}</h2>
          <div style={{ fontSize: 12, color: "#8a8576", marginBottom: 10 }}>
            {s.occasion}{s.liturgical_color ? ` · ${s.liturgical_color}` : ""}
          </div>
          {(s.readings || []).length > 0 && <KV label="Readings" value={s.readings.join(" · ")} />}
          {s.psalm && <KV label="Psalm" value={s.psalm} />}
          {(s.hymns || []).length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontWeight: 600, marginBottom: 4, color: "#1F2A44" }}>Hymns</div>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {s.hymns.map((h, j) => (
                  <li key={j} style={{ padding: "5px 0", borderBottom: "1px solid #f2ebd8", listStyle: "none", position: "relative", paddingLeft: 12 }}>
                    <span style={{ position: "absolute", left: 0, top: 5, color: "#B08A4A" }}>•</span>
                    {h.placement ? <b>{h.placement}: </b> : null}
                    {h.title}{h.tune ? <span style={{ color: "#7d7767" }}> ({h.tune})</span> : null}
                    {h.hymnal_source_id && h.number != null && h.availability_verified && (
                      <span style={{ color: "#1F2A44", fontSize: 13, marginLeft: 6, fontWeight: 500 }}>
                        {" "}— {h.hymnal_source_id} #{h.number}
                      </span>
                    )}
                    {!h.hymnal_source_id && h.source && h.source !== "verified_crosswalk" && h.source !== "no_verified_selection" && (
                      <span style={{ color: "#8a8576", fontSize: 13 }}> — {h.source}</span>
                    )}
                    {h.reason && <div style={{ color: "#6f6a5a", fontSize: 13, fontStyle: "italic", marginTop: 2 }}>{h.reason}</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(s.service_music || []).length > 0 && <KV label="Service Music" value={s.service_music.join(" · ")} />}
          {s.anthem_or_choir && <KV label="Anthem / Choir" value={s.anthem_or_choir} />}
          {Array.isArray(s.anthem_selections) && s.anthem_selections.length > 0 && (
            <div data-testid="report-anthem-selections" style={{ marginTop: 6 }}>
              <div style={{ fontWeight: 600, marginBottom: 4, color: "#1F2A44", fontSize: 13.5 }}>Selected Anthems</div>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {s.anthem_selections.map((a, k) => (
                  <li key={k} style={{ padding: "3px 0", listStyle: "none", position: "relative", paddingLeft: 12, fontSize: 13.5 }}>
                    <span style={{ position: "absolute", left: 0, top: 3, color: "#B08A4A" }}>•</span>
                    {a.liturgical_moment ? <b style={{ textTransform: "capitalize" }}>{a.liturgical_moment}: </b> : null}
                    {a.work_title}
                    {a.work_composer && <span style={{ color: "#7d7767" }}> — {a.work_composer}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {s.voluntaries && (s.voluntaries.prelude || s.voluntaries.postlude) && (
            <KV label="Voluntaries" value={`Prelude: ${s.voluntaries.prelude || "—"} · Postlude: ${s.voluntaries.postlude || "—"}`} />
          )}
          {s.liturgical_notes && <KV label="Liturgical Notes" value={s.liturgical_notes} />}
          {s.clergy_notes && <KV label="Clergy Notes" value={s.clergy_notes} />}
          {s.worship_team_notes && <KV label="Worship Team Notes" value={s.worship_team_notes} />}
          {s.liturgical_rubrics && <RubricsInsight rubrics={s.liturgical_rubrics} />}
        </div>
      ))}

      {plan.bulletin_copy && <Block title="Bulletin Copy" text={plan.bulletin_copy} />}
      {plan.continuity_notes && <Block title="Continuity Notes" text={plan.continuity_notes} />}
      {plan.seasonal_notes && <Block title="Seasonal Notes" text={plan.seasonal_notes} />}
      {(plan.checklist || []).length > 0 && <List title="Final Checklist" items={plan.checklist} />}
      {(plan.formation_opportunities || []).length > 0 && <List title="Bulletin & Formation Opportunities" items={plan.formation_opportunities} />}
      {(plan.risk_checklist || []).length > 0 && <List title="Risk & Verification Checklist" items={plan.risk_checklist} />}
      {(plan.strategic_recommendations || []).length > 0 && <List title="Strategic Recommendations" items={plan.strategic_recommendations} />}
    </div>
  );
}

function Block({ title, text }) {
  return (
    <div style={{ marginTop: 18 }}>
      <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 20, margin: "0 0 6px", color: "#1F2A44" }}>{title}</h2>
      <p style={{ lineHeight: 1.7, color: "#2a2a2a", margin: 0 }}>{text}</p>
    </div>
  );
}
function List({ title, items }) {
  return (
    <div style={{ marginTop: 18 }}>
      <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 20, margin: "0 0 6px", color: "#1F2A44" }}>{title}</h2>
      <ul style={{ margin: 0, paddingLeft: 20, color: "#2a2a2a", lineHeight: 1.6 }}>
        {items.map((c, i) => <li key={i}>{c}</li>)}
      </ul>
    </div>
  );
}
function KV({ label, value }) {
  return (
    <div style={{ display: "flex", gap: 10, padding: "4px 0", fontSize: 13.5 }}>
      <b style={{ minWidth: 130, color: "#1F2A44" }}>{label}</b>
      <span style={{ color: "#2a2a2a" }}>{value}</span>
    </div>
  );
}

function RubricsInsight({ rubrics }) {
  if (!rubrics || typeof rubrics !== "object") return null;
  const {
    rite, opening_acclamation, song_of_praise, penitential_order,
    confession_placement, creed, sequence_hymn, silence_points,
    ministerial_roles, prayers_of_the_people, sacristy_preparation, rubric_notes,
  } = rubrics;
  const has = (v) => v !== undefined && v !== null && v !== "";
  return (
    <div
      data-testid="rubrics-insight"
      style={{
        marginTop: 14,
        padding: 14,
        background: "#F4F1E6",
        border: "1px solid #d9cfae",
        borderRadius: 8,
      }}
    >
      <div style={{ fontWeight: 700, color: "#1F2A44", marginBottom: 6, fontSize: 13.5, letterSpacing: 0.3 }}>
        RUBRICS INSIGHT · Book of Common Prayer
      </div>
      {has(rite) && <KV label="Rite" value={rite} />}
      {opening_acclamation?.text && <KV label="Opening Acclamation" value={opening_acclamation.text} />}
      {song_of_praise?.kind && (
        <KV label="Song of Praise" value={`${song_of_praise.kind}${song_of_praise.reason ? ` — ${song_of_praise.reason}` : ""}`} />
      )}
      {penitential_order?.used && <KV label="Penitential Order" value="Used at the beginning" />}
      {has(confession_placement) && confession_placement !== "none" && (
        <KV label="Confession Placement" value={String(confession_placement).replace(/_/g, " ")} />
      )}
      {creed && has(creed.present) && (
        <KV label="Nicene Creed" value={creed.present ? "Included" : `Omitted${creed.omission_reason ? ` — ${creed.omission_reason}` : ""}`} />
      )}
      {sequence_hymn && <KV label="Sequence Hymn" value="Sung before the Gospel" />}
      {prayers_of_the_people?.form && (
        <KV label="Prayers of the People" value={`Form ${prayers_of_the_people.form}${prayers_of_the_people.intercessor ? ` · ${prayers_of_the_people.intercessor}` : ""}`} />
      )}
      {Array.isArray(silence_points) && silence_points.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontWeight: 600, color: "#1F2A44", fontSize: 13 }}>Sacred Silence</div>
          <ul style={{ margin: "4px 0 0", paddingLeft: 20, color: "#2a2a2a", fontSize: 13 }}>
            {silence_points.map((p, i) => (
              <li key={i}>
                {String(p.where || "").replace(/_/g, " ")}
                {p.duration_seconds ? ` — ${p.duration_seconds}s` : ""}
                {p.purpose ? ` · ${p.purpose}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
      {Array.isArray(ministerial_roles) && ministerial_roles.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontWeight: 600, color: "#1F2A44", fontSize: 13 }}>Ministerial Roles</div>
          <ul style={{ margin: "4px 0 0", paddingLeft: 20, color: "#2a2a2a", fontSize: 13 }}>
            {ministerial_roles.map((r, i) => (
              <li key={i}>
                <b>{r.role}</b>{r.assigned_to ? ` — ${r.assigned_to}` : ""}
                {r.notes ? ` · ${r.notes}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
      {has(sacristy_preparation) && <KV label="Sacristy Preparation" value={sacristy_preparation} />}
      {Array.isArray(rubric_notes) && rubric_notes.length > 0 && (
        <ul style={{ margin: "6px 0 0", paddingLeft: 20, color: "#4a4736", fontSize: 12.5 }}>
          {rubric_notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      )}
    </div>
  );
}
